import {useCallback, useEffect, useRef, useState} from 'react';

interface UseWalletGridKeyboardNavigationOptions {
  itemCount: number;
  columns: number;
  onClose: () => void;
  onFocusSearchInput: () => void;
}

interface UseWalletGridKeyboardNavigationResult {
  focusedIndex: number;
  setFocusedIndex: (index: number) => void;
  handleKeyDown: (event: React.KeyboardEvent) => void;
  resetFocus: () => void;
}

export const useWalletGridKeyboardNavigation = ({
  itemCount,
  columns,
  onClose,
  onFocusSearchInput,
}: UseWalletGridKeyboardNavigationOptions): UseWalletGridKeyboardNavigationResult => {
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const lastActionRef = useRef<'user' | 'reset' | null>(null);

  useEffect(() => {
    if (lastActionRef.current === 'user' && focusedIndex === -1) {
      onFocusSearchInput();
    }
  }, [focusedIndex, onFocusSearchInput]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const {key} = event;

      if (key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      let newIndex = focusedIndex;
      lastActionRef.current = 'user';

      switch (key) {
        case 'ArrowDown':
          event.preventDefault();
          if (focusedIndex === -1) {
            newIndex = 0;
          } else if (focusedIndex + columns < itemCount) {
            newIndex = focusedIndex + columns;
          }
          break;

        case 'ArrowUp':
          event.preventDefault();
          if (focusedIndex < columns && focusedIndex >= 0) {
            newIndex = -1;
          } else if (focusedIndex >= columns) {
            newIndex = focusedIndex - columns;
          }
          break;

        case 'ArrowRight':
          if (focusedIndex >= 0 && focusedIndex < itemCount - 1) {
            event.preventDefault();
            newIndex = focusedIndex + 1;
          }
          break;

        case 'ArrowLeft':
          if (focusedIndex >= 0) {
            event.preventDefault();
            newIndex = focusedIndex - 1;
          }
          break;

        default:
          return;
      }

      setFocusedIndex(newIndex);
    },
    [focusedIndex, itemCount, columns, onClose],
  );

  const resetFocus = useCallback(() => {
    lastActionRef.current = 'reset';
    setFocusedIndex(-1);
  }, []);

  return {
    focusedIndex,
    setFocusedIndex,
    handleKeyDown,
    resetFocus,
  };
};
